import { useCallback, useEffect, type CSSProperties } from "react";
import { dialogTitleStyle } from "./destructiveStyles";

interface SaveConflictDialogProps {
  busy: boolean;
  error?: string | null;
  onReload: () => void;
  onOverwrite: () => void;
  onClose: () => void;
}

export default function SaveConflictDialog({
  busy,
  error,
  onReload,
  onOverwrite,
  onClose,
}: SaveConflictDialogProps) {
  const close = useCallback(() => {
    if (!busy) onClose();
  }, [busy, onClose]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [close]);

  return (
    <div style={overlayStyle} role="presentation" onClick={close}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="save-conflict-title"
        aria-describedby="save-conflict-description"
        style={dialogStyle}
        data-testid="save-conflict-dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="save-conflict-title" style={dialogTitleStyle}>
          Drawing changed elsewhere
        </h2>
        <div style={bodyStyle}>
          <p id="save-conflict-description" style={messageStyle}>
            A newer cloud version exists. Reloading discards your current unsaved changes;
            overwriting replaces the newer cloud version with what is currently on this canvas.
          </p>
          <div style={buttonRowStyle}>
            {error && (
              <p role="alert" style={errorStyle}>
                {error}
              </p>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={onReload}
              style={{ ...secondaryButtonStyle, ...(busy ? disabledButtonStyle : null) }}
            >
              Reload latest
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={onOverwrite}
              style={{ ...secondaryButtonStyle, ...(busy ? disabledButtonStyle : null) }}
            >
              {busy ? "Overwriting…" : "Overwrite cloud version"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const EXCALIDRAW_FONT =
  "Assistant, system-ui, BlinkMacSystemFont, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif";

const MODAL_SHADOW = "var(--exms-shadow-modal)";

const overlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 10003,
  background: "var(--exms-overlay-bg)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};

const dialogStyle: CSSProperties = {
  width: "100%",
  maxWidth: 560,
  boxSizing: "border-box",
  background: "var(--island-bg-color)",
  border: "1px solid var(--dialog-border-color)",
  borderRadius: 12,
  boxShadow: MODAL_SHADOW,
  fontFamily: EXCALIDRAW_FONT,
  padding: 24,
};

const bodyStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 16,
  marginTop: 20,
};

const messageStyle: CSSProperties = { margin: 0, fontSize: 15, lineHeight: 1.5 };

const errorStyle: CSSProperties = {
  margin: 0,
  flex: 1,
  minWidth: 0,
  alignSelf: "center",
  color: "var(--color-danger)",
  fontSize: 13,
};

const buttonRowStyle: CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  flexWrap: "wrap",
  gap: 12,
};

const secondaryButtonStyle: CSSProperties = {
  borderRadius: 10,
  border: "1px solid var(--text-primary-color)",
  padding: "12px 20px",
  cursor: "pointer",
  fontSize: 15,
  fontFamily: "inherit",
  fontWeight: 700,
  whiteSpace: "nowrap",
  background: "var(--island-bg-color)",
  color: "inherit",
};

const disabledButtonStyle: CSSProperties = {
  cursor: "not-allowed",
  opacity: 0.6,
};
